import { HttpClient, httpResource } from '@angular/common/http';
import { Injectable, inject, signal, computed } from '@angular/core';
import { forkJoin, of, tap, map, Observable } from 'rxjs';
import {
  type AddMember,
  type CreateInvitation,
  type InvitationResponse,
  type LinkPerson,
  type UnassignedGroup,
  type UnlinkedPerson,
  type UpdateUserRoles,
  type UpdateUserStatus,
  type UserGroup,
  type UserDetailsResponse,
  type UsersResponse,
} from '@zag/auth/models';
import { PermissionService } from './permission.service';

export type {
  CreateInvitation,
  InvitationResponse,
  LinkedPerson,
  ManagedUser,
  ManagedUserDetails,
  UnassignedGroup,
  UnlinkedPerson,
  UserGroup,
  UserPermission,
  UserRole,
} from '@zag/auth/models';

/** Represents a group that has been added locally but not yet saved */
export interface LocalGroupAddition {
  groupId: string;
  isAdmin: boolean;
  /** Group info for display purposes */
  group: UnassignedGroup;
}

@Injectable({ providedIn: 'root' })
export class UsersManagementService {
  private readonly http = inject(HttpClient);
  private readonly permissionService = inject(PermissionService);
  private readonly basePath = '/api/v1/rbac/users';
  private readonly invitationsPath = '/api/v1/users/invitations';
  private readonly canReadUsers = computed(() => this.permissionService.hasPermission('rbac:users:read'));

  // ============================================================================
  // User List Management
  // ============================================================================

  private readonly refreshTrigger = signal(0);
  private readonly selectedRefreshTrigger = signal(0);
  readonly selectedId = signal<string | undefined>(undefined);

  private readonly listResource = httpResource<UsersResponse>(() => {
    this.refreshTrigger();
    return this.canReadUsers() ? this.basePath : undefined;
  });

  private readonly selectedResource = httpResource<UserDetailsResponse>(() => {
    const id = this.selectedId();
    this.selectedRefreshTrigger(); // Subscribe to refresh trigger
    return id ? `${this.basePath}/${id}` : undefined;
  });

  readonly users = computed(() => this.listResource.value() ?? []);
  readonly isLoading = computed(() => this.listResource.isLoading());
  readonly error = computed(() => this.listResource.error());

  readonly selectedUser = computed(() => this.selectedResource.value());
  readonly isLoadingSelected = computed(() => this.selectedResource.isLoading());

  reload(): void {
    this.refreshTrigger.update(v => v + 1);
  }

  reloadSelected(): void {
    this.selectedRefreshTrigger.update(v => v + 1);
  }

  selectById(id: string): void {
    this.selectedId.set(id);
    // Trigger group reload when user is selected
    this.groupsRefreshTrigger.update(v => v + 1);
  }

  clearSelection(): void {
    this.selectedId.set(undefined);
    this.clearLocalGroupChanges();
  }

  updateUserRoles(userId: string, roleIds: string[]) {
    const payload: UpdateUserRoles = { roleIds };
    return this.http.put<UserDetailsResponse>(`${this.basePath}/${userId}/roles`, payload);
  }

  updateUserStatus(userId: string, active: boolean): Observable<UserDetailsResponse> {
    const payload: UpdateUserStatus = { active };
    return this.http.patch<UserDetailsResponse>(`${this.basePath}/${userId}/status`, payload).pipe(
      tap(() => {
        this.reload();
        this.selectedRefreshTrigger.update(v => v + 1);
      })
    );
  }

  // ============================================================================
  // Group Management - Reactive Resources
  // ============================================================================

  private readonly groupsRefreshTrigger = signal(0);

  /** Resource for fetching unassigned groups */
  private readonly unassignedGroupsResource = httpResource<UnassignedGroup[]>(() => {
    const userId = this.selectedId();
    this.groupsRefreshTrigger(); // Subscribe to refresh trigger
    return userId ? `${this.basePath}/${userId}/groups/unassigned` : undefined;
  });

  /** Groups from selected user (already saved) */
  readonly groups = computed(() => this.selectedUser()?.groups ?? []);
  readonly groupsLoading = computed(() => this.selectedResource.isLoading());

  /** Unassigned groups from backend */
  private readonly backendUnassignedGroups = computed(() => this.unassignedGroupsResource.value() ?? []);
  readonly unassignedGroupsLoading = computed(() => this.unassignedGroupsResource.isLoading());

  // ============================================================================
  // Group Management - Local State for Unsaved Changes
  // ============================================================================

  /** Track groups added locally (not yet saved to backend) */
  private readonly _localAddedGroups = signal<LocalGroupAddition[]>([]);

  /** Track groups removed locally (not yet saved to backend) */
  private readonly _localRemovedGroupIds = signal<Set<string>>(new Set());

  /** Read-only access to local additions */
  readonly localAddedGroups = this._localAddedGroups.asReadonly();

  /** Check if there are unsaved group changes */
  readonly hasUnsavedGroupChanges = computed(() => {
    return this._localAddedGroups().length > 0 || this._localRemovedGroupIds().size > 0;
  });

  /**
   * Combined view of all groups including local additions
   * Excludes groups marked for removal
   */
  readonly allGroups = computed(() => {
    const backendGroups = this.groups();
    const localAdded = this._localAddedGroups();
    const localRemoved = this._localRemovedGroupIds();

    // Filter out groups marked for removal
    const filteredBackend = backendGroups.filter(g => !localRemoved.has(g.id));

    // Convert local additions to UserGroup format for display
    const localAsGroups: UserGroup[] = localAdded.map(local => ({
      id: local.group.id,
      name: local.group.name,
      description: local.group.description,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isAdmin: local.isAdmin,
    }));

    return [...filteredBackend, ...localAsGroups];
  });

  /**
   * Available unassigned groups (excluding locally added ones)
   */
  readonly availableUnassignedGroups = computed(() => {
    const unassigned = this.backendUnassignedGroups();
    const localAddedIds = new Set(this._localAddedGroups().map(g => g.groupId));
    // Also include groups that were removed locally (they become available again)
    const localRemovedIds = this._localRemovedGroupIds();

    // Filter out locally added groups from the unassigned list
    const filtered = unassigned.filter(g => !localAddedIds.has(g.id));

    // Add back groups that were removed locally (if they're not in the filtered list)
    const backendGroups = this.groups();
    const removedGroups = backendGroups
      .filter(g => localRemovedIds.has(g.id))
      .map(g => ({
        id: g.id,
        name: g.name,
        description: g.description,
      }));

    // Avoid duplicates
    const existingIds = new Set(filtered.map(g => g.id));
    const additionalGroups = removedGroups.filter(g => !existingIds.has(g.id));

    return [...filtered, ...additionalGroups];
  });

  /**
   * Reload groups and unassigned groups
   */
  reloadGroups(): void {
    this.groupsRefreshTrigger.update(v => v + 1);
  }

  /**
   * Add a group locally (not saved to backend yet)
   */
  addGroupLocally(group: UnassignedGroup, isAdmin = false): void {
    const current = this._localAddedGroups();
    const removedIds = this._localRemovedGroupIds();

    // If this group was marked for removal, unmark it instead
    if (removedIds.has(group.id)) {
      const newRemoved = new Set(removedIds);
      newRemoved.delete(group.id);
      this._localRemovedGroupIds.set(newRemoved);
      return;
    }

    // Avoid duplicates
    if (!current.some(g => g.groupId === group.id)) {
      this._localAddedGroups.set([...current, { groupId: group.id, isAdmin, group }]);
    }
  }

  /**
   * Remove a group locally (mark for removal, not saved yet)
   * For groups from backend: marks them for deletion
   * For local additions: removes them from local list
   */
  removeGroupLocally(groupId: string): void {
    const localAdded = this._localAddedGroups();
    const localAddition = localAdded.find(g => g.groupId === groupId);

    if (localAddition) {
      // It's a local addition - just remove from local list
      this._localAddedGroups.set(localAdded.filter(g => g.groupId !== groupId));
    } else {
      // It's a backend group - mark for removal
      const removedIds = this._localRemovedGroupIds();
      const newRemoved = new Set(removedIds);
      newRemoved.add(groupId);
      this._localRemovedGroupIds.set(newRemoved);
    }
  }

  /**
   * Clear all local group changes
   */
  clearLocalGroupChanges(): void {
    this._localAddedGroups.set([]);
    this._localRemovedGroupIds.set(new Set());
  }

  /**
   * Save all local group changes to backend
   * Returns an observable that completes when all changes are saved
   */
  saveGroupChanges(userId: string): Observable<void> {
    const localAdded = this._localAddedGroups();
    const localRemoved = this._localRemovedGroupIds();

    if (localAdded.length === 0 && localRemoved.size === 0) {
      return of(undefined); // Nothing to save
    }

    const operations: Observable<unknown>[] = [];

    // Add new groups
    for (const group of localAdded) {
      operations.push(this.addUserToGroup(userId, group.groupId, group.isAdmin));
    }

    // Remove groups
    for (const groupId of localRemoved) {
      operations.push(this.removeUserFromGroup(userId, groupId));
    }

    return forkJoin(operations).pipe(
      tap(() => {
        this.clearLocalGroupChanges();
        this.reloadGroups();
        this.reload(); // Refresh user list to update group counts
        // Re-fetch user details to get updated groups
        this.selectedRefreshTrigger.update(v => v + 1);
      }),
      map(() => undefined)
    );
  }

  // ============================================================================
  // Group Management - HTTP Methods
  // ============================================================================

  addUserToGroup(userId: string, groupId: string, isAdmin = false) {
    const payload: AddMember = { userId, isAdmin };
    return this.http.post<void>(`/api/v1/rbac/groups/${groupId}/members`, payload);
  }

  removeUserFromGroup(userId: string, groupId: string) {
    return this.http.delete<void>(`/api/v1/rbac/groups/${groupId}/members/${userId}`);
  }

  // ============================================================================
  // Person Management - Reactive Resources
  // ============================================================================

  private readonly personsRefreshTrigger = signal(0);

  /** Resource for fetching unlinked persons */
  private readonly unlinkedPersonsResource = httpResource<UnlinkedPerson[]>(() => {
    this.personsRefreshTrigger();
    return this.canReadUsers() ? `${this.basePath}/persons/unlinked` : undefined;
  });

  /** Linked person from selected user */
  readonly linkedPerson = computed(() => this.selectedUser()?.linkedPerson ?? null);

  /** Unlinked persons from backend */
  readonly unlinkedPersons = computed(() => this.unlinkedPersonsResource.value() ?? []);
  readonly unlinkedPersonsLoading = computed(() => this.unlinkedPersonsResource.isLoading());

  /**
   * Reload unlinked persons
   */
  reloadPersons(): void {
    this.personsRefreshTrigger.update(v => v + 1);
  }

  // ============================================================================
  // Person Management - HTTP Methods
  // ============================================================================

  /**
   * Link a person to a user
   */
  linkPerson(userId: string, personId: string): Observable<UserDetailsResponse> {
    const payload: LinkPerson = { personId };
    return this.http.post<UserDetailsResponse>(`${this.basePath}/${userId}/person`, payload).pipe(
      tap(() => {
        this.reloadPersons();
        this.reload();
        this.selectedRefreshTrigger.update(v => v + 1);
      })
    );
  }

  /**
   * Unlink a person from a user
   */
  unlinkPerson(userId: string): Observable<UserDetailsResponse> {
    return this.http.delete<UserDetailsResponse>(`${this.basePath}/${userId}/person`).pipe(
      tap(() => {
        this.reloadPersons();
        this.reload();
        this.selectedRefreshTrigger.update(v => v + 1);
      })
    );
  }

  /**
   * Reassign a person to a user (unlink current person and link new one)
   */
  reassignPerson(userId: string, newPersonId: string): Observable<UserDetailsResponse> {
    const payload: LinkPerson = { personId: newPersonId };
    return this.http.put<UserDetailsResponse>(`${this.basePath}/${userId}/person`, payload).pipe(
      tap(() => {
        this.reloadPersons();
        this.reload();
        this.selectedRefreshTrigger.update(v => v + 1);
      })
    );
  }

  // ============================================================================
  // Invitation Management - Reactive Resources
  // ============================================================================

  private readonly invitationsRefreshTrigger = signal(0);

  /** Resource for fetching pending invitations */
  private readonly invitationsResource = httpResource<InvitationResponse[]>(() => {
    this.invitationsRefreshTrigger();
    return this.canReadUsers() ? this.invitationsPath : undefined;
  });

  /**
   * Get a single invitation by ID.
   */
  getInvitation(id: string): Observable<InvitationResponse> {
    return this.http.get<InvitationResponse>(`${this.invitationsPath}/${id}`);
  }

  /** Pending invitations from backend */
  readonly invitations = computed(() => this.invitationsResource.value() ?? []);
  readonly invitationsLoading = computed(() => this.invitationsResource.isLoading());

  /**
   * Reload pending invitations
   */
  reloadInvitations(): void {
    this.invitationsRefreshTrigger.update(v => v + 1);
  }

  // ============================================================================
  // Invitation Management - HTTP Methods
  // ============================================================================

  /**
   * Create a new invitation
   */
  createInvitation(data: CreateInvitation): Observable<{ data: InvitationResponse }> {
    return this.http.post<{ data: InvitationResponse }>(this.invitationsPath, data).pipe(
      tap(() => {
        this.reloadInvitations();
        this.reload(); // Refresh user list in case user already exists
      })
    );
  }

  /**
   * Resend an invitation (regenerates token and sends a new email)
   */
  resendInvitation(id: string): Observable<{ data: InvitationResponse }> {
    return this.http.post<{ data: InvitationResponse }>(`${this.invitationsPath}/${id}/resends`, {}).pipe(
      tap(() => {
        this.reloadInvitations();
      })
    );
  }

  /**
   * Revoke an invitation
   */
  revokeInvitation(id: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.invitationsPath}/${id}`).pipe(
      tap(() => {
        this.reloadInvitations();
      })
    );
  }

  /**
   * Permanently delete a user account.
   * The backend revokes all active sessions before deleting.
   */
  deleteUser(id: string): Observable<void> {
    return this.http.delete<void>(`${this.basePath}/${id}`).pipe(
      tap(() => {
        this.reload();
      })
    );
  }

  /**
   * Trigger a password-reset email for a user (admin action).
   * The backend issues a reset token and emails the user a link to set a new
   * password. No state changes locally, so no reload is needed.
   */
  sendPasswordReset(id: string): Observable<{ data: { status: string } }> {
    return this.http.post<{ data: { status: string } }>(`${this.basePath}/${id}/password-resets`, {});
  }

  setMustChangePassword(id: string, mustChangePassword: boolean): Observable<UserDetailsResponse> {
    return this.http
      .patch<UserDetailsResponse>(`${this.basePath}/${id}/must-change-password`, { mustChangePassword })
      .pipe(tap(() => this.reloadSelected()));
  }
}
